import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2, Plus, Eye, EyeOff, Edit3, Trash2,
  Briefcase, FileSignature, Receipt, Calendar, FileText,
  KeyRound, ExternalLink, ChevronDown, ChevronUp, Sparkles, X, Loader2,
  CheckCircle2, FolderUp, Download, Upload, File as FileIcon, FileImage,
  LayoutDashboard, ArrowRight, ListChecks, Target,
} from "lucide-react";
import { isPortalAuthed, clearPortalAuth, getPortalEntrepreneur } from "@/lib/portalAuth";
import { SERVICES } from "@/data/services";
import PortalSidebar from "@/components/portal/PortalSidebar";

export default function PortalHome() {
  const nav = useNavigate();
  const [me, setMe] = useState(getPortalEntrepreneur());
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [aggregate, setAggregate] = useState({ proposals: [], invoices: [], appointments: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [section, setSection] = useState("dashboard");
  const [scopeCompanyId, setScopeCompanyId] = useState("all");

  useEffect(() => {
    if (!isPortalAuthed()) { nav("/portail/connexion", { replace: true }); return; }
    api.get("/portal/me").then((r) => setMe(r.data)).catch(() => { clearPortalAuth(); nav("/portail/connexion"); });
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const c = await api.get("/portal/companies");
      const cList = c.data || [];
      setCompanies(cList);
      if (!activeCompanyId && cList.length) setActiveCompanyId(cList[0].id);
      // Fetch ALL appointments (including pre-account ones matched by email) — runs in parallel
      const allApptsPromise = api.get("/portal/appointments").then((r) => r.data || []).catch(() => []);
      // Aggregate proposals / invoices / appointments / projects across all companies
      if (cList.length) {
        const details = await Promise.all(cList.map((co) => api.get(`/portal/companies/${co.id}`).then((r) => r.data).catch(() => null)));
        const props = [], invs = [], appts = [], projs = [];
        details.forEach((d) => {
          if (!d) return;
          (d.proposals || []).forEach((p) => props.push({ ...p, _company: d.company?.name, _company_id: d.company?.id }));
          (d.invoices || []).forEach((i) => invs.push({ ...i, _company: d.company?.name, _company_id: d.company?.id }));
          (d.appointments || []).forEach((a) => appts.push({ ...a, _company: d.company?.name, _company_id: d.company?.id }));
          (d.projects || []).forEach((pr) => projs.push({ ...pr, _company: d.company?.name, _company_id: d.company?.id }));
        });
        // Merge global appointments — surfaces any RDV booked BEFORE the account or before companies were added
        const globalAppts = await allApptsPromise;
        const seen = new Set(appts.map((a) => a.id));
        globalAppts.forEach((a) => { if (!seen.has(a.id)) appts.push({ ...a, _company: a.company || null, _company_id: a.company_id || null }); });
        setAggregate({ proposals: props, invoices: invs, appointments: appts, documents: [] });
        setProjects(projs);
      } else {
        const globalAppts = await allApptsPromise;
        setAggregate({ proposals: [], invoices: [], appointments: globalAppts.map((a) => ({ ...a, _company: a.company || null, _company_id: null })), documents: [] });
        setProjects([]);
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    companies: companies.length,
    projects: projects.length,
    proposals: aggregate.proposals.length,
    invoices: aggregate.invoices.length,
    appointments: aggregate.appointments.length,
  }), [companies, projects, aggregate]);

  /* Apply company scope filter to all aggregates */
  const scope = useMemo(() => {
    if (scopeCompanyId === "all") return { projects, ...aggregate, companies };
    const filterByCompanyId = (arr) => arr.filter((x) => x._company_id === scopeCompanyId);
    return {
      companies: companies.filter((c) => c.id === scopeCompanyId),
      projects: filterByCompanyId(projects),
      proposals: filterByCompanyId(aggregate.proposals),
      invoices: filterByCompanyId(aggregate.invoices),
      appointments: filterByCompanyId(aggregate.appointments),
      documents: aggregate.documents,
    };
  }, [scopeCompanyId, projects, aggregate, companies]);

  /* Auto switch active company in detail view when scope changes */
  useEffect(() => {
    if (scopeCompanyId !== "all" && activeCompanyId !== scopeCompanyId) setActiveCompanyId(scopeCompanyId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeCompanyId]);

  const openAddCompany = () => { setEditingCompany(null); setShowCompanyModal(true); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F7FB] via-white to-[#F0F4FA]" data-testid="portal-home">
      <PortalSidebar
        active={section}
        onChange={setSection}
        counts={counts}
        onAddCompany={openAddCompany}
        companies={companies}
        activeCompanyId={scopeCompanyId}
        onCompanyChange={setScopeCompanyId}
      />

      <main className="lg:pl-72 min-h-screen">
        <div className="px-3 py-3 sm:px-4 sm:py-4">
            {loading ? (
              <div className="bg-white rounded-2xl p-10 text-center text-sm text-[#64748B]">
                <Loader2 className="animate-spin inline mr-2" />Chargement de votre espace…
              </div>
            ) : (
              <>
                {section === "dashboard" && (
                  <DashboardSection me={me} companies={scope.companies} projects={scope.projects} aggregate={{ proposals: scope.proposals, invoices: scope.invoices, appointments: scope.appointments }} onAddCompany={openAddCompany} onGoTo={setSection} scopeCompanyId={scopeCompanyId} />
                )}
                {section === "companies" && (
                  <CompaniesSection
                    companies={companies}
                    activeCompanyId={activeCompanyId}
                    setActiveCompanyId={setActiveCompanyId}
                    onAddCompany={openAddCompany}
                    onEditCompany={(c) => { setEditingCompany(c); setShowCompanyModal(true); }}
                    onChange={refresh}
                  />
                )}
                {section === "projects" && (
                  <ProjectsSection projects={scope.projects} />
                )}
                {section === "documents" && (
                  <DocumentsAllSection companies={scope.companies} />
                )}
                {section === "appointments" && (
                  <AppointmentsSection items={scope.appointments} />
                )}
                {section === "proposals" && (
                  <ProposalsSection items={scope.proposals} />
                )}
                {section === "invoices" && (
                  <InvoicesSection items={scope.invoices} />
                )}
                {section === "account" && (
                  <AccountSection me={me} onUpdated={(u) => setMe(u)} />
                )}
              </>
            )}
          </div>
        </main>

      {showCompanyModal && (
        <CompanyModal company={editingCompany} onClose={() => setShowCompanyModal(false)} onSaved={() => { setShowCompanyModal(false); refresh(); }} />
      )}
    </div>
  );
}

/* ===== Section: Dashboard ===== */
function DashboardSection({ me, companies, projects, aggregate, onAddCompany, onGoTo }) {
  const firstName = (me?.full_name || "").split(" ")[0] || "Entrepreneur";
  const stats = [
    { key: "companies", label: "Entreprises", value: companies.length, icon: Building2, color: "#06B6D4" },
    { key: "projects", label: "Projets", value: projects.length, icon: FileText, color: "#FFDCA8" },
    { key: "proposals", label: "Devis", value: aggregate.proposals.length, icon: FileSignature, color: "#A7F3D0" },
    { key: "invoices", label: "Factures", value: aggregate.invoices.length, icon: Receipt, color: "#67E8F9" },
  ];

  return (
    <div className="space-y-6" data-testid="portal-section-dashboard">
      <div className="bg-white rounded-3xl p-6 border border-black/8">
        <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Mon espace</div>
        <h1 className="display text-3xl sm:text-4xl font-extrabold text-[#0A1128] mt-1">
          Bonjour {firstName}
        </h1>
        <p className="text-sm text-[#64748B] mt-2 max-w-xl">
          Pilotez vos entreprises, suivez vos projets en cours, vos devis et vos factures au même endroit. L'équipe BESSON est à vos côtés.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={onAddCompany} className="bg-[#0A1128] text-white hover:bg-[#14213d]" data-testid="dashboard-add-company">
            <Plus size={14} className="mr-1" />Ajouter une entreprise
          </Button>
          <Button onClick={() => onGoTo("appointments")} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold" data-testid="dashboard-book-rdv">
            <Calendar size={14} className="mr-1" />Prendre RDV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <button key={s.key} onClick={() => onGoTo(s.key)} data-testid={`dashboard-stat-${s.key}`} className="bg-white rounded-2xl p-4 border border-black/8 text-left hover:border-[#06B6D4] transition-colors group">
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase" style={{ color: s.color }}>
              <s.icon size={12} /> {s.label}
            </div>
            <div className="display text-3xl font-extrabold text-[#0A1128] mt-1">{s.value}</div>
            <div className="text-[11px] text-[#64748B] mt-1 inline-flex items-center gap-1 group-hover:text-[#06B6D4]">Voir <ArrowRight size={11} /></div>
          </button>
        ))}
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentList title="Derniers devis" icon={FileSignature} items={aggregate.proposals.slice(0, 4)} empty="Aucun devis pour l'instant." render={(p) => (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold text-[#0A1128]">{p.ref}</div>
              <div className="text-xs text-[#64748B]">{p._company} · {(p.totals?.total_ttc || 0).toFixed(2)} € TTC</div>
            </div>
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">{p.status}</span>
          </div>
        )} />
        <RecentList title="Prochains rendez-vous" icon={Calendar} items={aggregate.appointments.slice(0, 4)} empty="Aucun rendez-vous planifié." render={(a) => (
          <div>
            <div className="font-bold text-[#0A1128]">{a.type === "visio_1h" ? "Visio 1h" : a.type === "phone_15m" ? "Téléphone 15 min" : "Physique"}</div>
            <div className="text-xs text-[#64748B]">{a._company} · {a.preferred_date} {a.preferred_time}</div>
          </div>
        )} />
      </div>
    </div>
  );
}

function RecentList({ title, icon: Icon, items, empty, render }) {
  return (
    <div className="bg-white rounded-3xl p-5 border border-black/8">
      <div className="flex items-center gap-2 mb-3"><Icon size={16} className="text-[#06B6D4]" /><span className="display font-extrabold text-[#0A1128]">{title}</span></div>
      {items.length === 0 ? (
        <div className="text-sm text-[#64748B] py-4 text-center">{empty}</div>
      ) : (
        <ul className="divide-y divide-black/5">
          {items.map((it, i) => <li key={it.id || i} className="py-3">{render(it)}</li>)}
        </ul>
      )}
    </div>
  );
}

/* ===== Section: Companies (table + quick switcher) ===== */
function CompaniesSection({ companies, activeCompanyId, setActiveCompanyId, onAddCompany, onEditCompany, onChange }) {
  const [mode, setMode] = useState("table"); // 'table' | 'detail'

  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-dashed border-[#06B6D4]/40 p-10 text-center" data-testid="portal-empty">
        <Building2 className="mx-auto text-[#06B6D4]" size={48} />
        <h2 className="display text-xl font-extrabold text-[#0A1128] mt-3">Ajoutez votre première entreprise</h2>
        <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">Créez une carte par entreprise pour centraliser ses coordonnées, ses accès et le suivi des prestations BESSON.</p>
        <Button onClick={onAddCompany} className="mt-5 bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold">
          <Plus size={14} className="mr-1" />Créer ma première carte
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="portal-section-companies">
      {/* Header + view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="display text-2xl font-extrabold text-[#0A1128]">Mes entreprises</h2>
          <p className="text-sm text-[#64748B]">{companies.length} {companies.length > 1 ? "entreprises" : "entreprise"} suivie{companies.length > 1 ? "s" : ""} avec BESSON.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-[#FAFAFA] border border-black/8 p-1">
            <button onClick={() => setMode("table")} data-testid="cies-view-table" className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${mode === "table" ? "bg-[#0A1128] text-white" : "text-[#64748B] hover:text-[#0A1128]"}`}>Tableau</button>
            <button onClick={() => setMode("detail")} data-testid="cies-view-detail" className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${mode === "detail" ? "bg-[#0A1128] text-white" : "text-[#64748B] hover:text-[#0A1128]"}`}>Détail</button>
          </div>
          <Button onClick={onAddCompany} className="bg-[#0A1128] hover:bg-[#14213d] text-white" data-testid="cies-add-btn"><Plus size={14} className="mr-1.5" />Ajouter</Button>
        </div>
      </div>

      {/* Quick switcher chips (always shown) */}
      <div className="flex flex-wrap items-center gap-2">
        {companies.map((c) => {
          const isActive = activeCompanyId === c.id;
          return (
            <button
              key={c.id}
              onClick={() => { setActiveCompanyId(c.id); setMode("detail"); }}
              data-testid={`cies-chip-${c.id}`}
              className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-all ${isActive ? "bg-[#06B6D4] border-[#06B6D4] text-[#0A1128] shadow-md" : "bg-white border-black/8 hover:border-[#06B6D4]/60 text-[#0A1128]"}`}
            >
              {c.logo_url ? (
                <img src={c.logo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A1128] text-[#06B6D4] font-extrabold text-[11px]">{(c.name || "?").slice(0, 1).toUpperCase()}</span>
              )}
              <span className="text-sm font-bold max-w-[140px] truncate">{c.name}</span>
            </button>
          );
        })}
      </div>

      {mode === "table" ? (
        <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B]">
                <tr>
                  <th className="text-left px-4 py-2.5">Entreprise</th>
                  <th className="text-left px-4 py-2.5">Secteur</th>
                  <th className="text-left px-4 py-2.5">SIRET</th>
                  <th className="text-left px-4 py-2.5">Contact</th>
                  <th className="text-left px-4 py-2.5">Site</th>
                  <th className="text-right px-4 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} data-testid={`cies-row-${c.id}`} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {c.logo_url ? (
                          <img src={c.logo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[#06B6D4]/15" />
                        ) : (
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0A1128] text-[#06B6D4] font-extrabold text-sm">{(c.name || "?").slice(0, 1).toUpperCase()}</span>
                        )}
                        <div className="min-w-0">
                          <div className="display font-extrabold text-[#0A1128] truncate">{c.name}</div>
                          {c.tva && <div className="text-[10px] text-[#64748B]">TVA {c.tva}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#475569]">{c.sector || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#475569] tabular-nums">{c.siret || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {c.email && <div className="text-[#0A1128] truncate max-w-[180px]">{c.email}</div>}
                      {c.phone && <div className="text-[#64748B]">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline inline-flex items-center gap-0.5 truncate max-w-[140px]">{c.website.replace(/^https?:\/\//, "")} <ExternalLink size={10} /></a> : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => { setActiveCompanyId(c.id); setMode("detail"); }} data-testid={`cies-row-open-${c.id}`} className="text-[#06B6D4] hover:underline text-xs font-bold">Ouvrir →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        activeCompanyId && <CompanyDetail companyId={activeCompanyId} onEdit={onEditCompany} onChange={onChange} />
      )}
    </div>
  );
}

/* ===== Section: Projects ===== */
const PROJECT_STATUS_LABEL = {
  brouillon: { l: "Brouillon", c: "bg-slate-200 text-slate-700" },
  en_cours: { l: "En cours", c: "bg-[#06B6D4] text-white" },
  livre: { l: "Livré", c: "bg-blue-500 text-white" },
  cloture: { l: "Clôturé", c: "bg-green-600 text-white" },
  en_attente: { l: "En attente", c: "bg-[#FFDCA8] text-[#92400E]" },
  annule: { l: "Annulé", c: "bg-red-500 text-white" },
};

function ProjectsSection({ projects }) {
  const [activeProject, setActiveProject] = useState(null);
  return (
    <div className="space-y-4" data-testid="portal-section-projects">
      <div>
        <h2 className="display text-2xl font-extrabold text-[#0A1128]">Mes projets en cours</h2>
        <p className="text-sm text-[#64748B]">Suivez en temps réel l'avancement des projets confiés à l'équipe BESSON.</p>
      </div>
      {projects.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center border border-dashed border-black/15" data-testid="portal-projects-empty">
          <FileText className="mx-auto text-[#06B6D4]" size={40} />
          <p className="text-sm text-[#64748B] mt-3">Aucun projet en cours pour l'instant.</p>
          <p className="text-xs text-[#94a3b8] mt-1">Un projet est créé automatiquement à la signature d'un devis.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projects.map((p) => {
            const st = PROJECT_STATUS_LABEL[p.status] || { l: p.status || "—", c: "bg-slate-200 text-slate-700" };
            const pct = Number(p.progress_pct || 0);
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => setActiveProject(p)}
                className="text-left bg-white rounded-3xl border border-black/8 p-5 hover:border-[#06B6D4]/40 hover:shadow-md transition-all"
                data-testid={`portal-project-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="display font-extrabold text-[#0A1128] text-lg">{p.title || "Projet"}</div>
                    {p._company && <div className="text-xs text-[#64748B] mt-0.5">{p._company}</div>}
                  </div>
                  <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${st.c}`}>{st.l}</span>
                </div>
                {p.description && <p className="text-sm text-[#475569] mt-2 line-clamp-2">{p.description}</p>}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-[#64748B]">Avancement</span>
                    <span className="font-bold text-[#0A1128]">{pct}% — {p.tasks_done || 0}/{p.tasks_total || 0} tâches</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#FAFAFA] border border-black/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#06B6D4] to-[#67E8F9] transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-[#64748B] flex-wrap">
                  {p.created_at && <span>Démarrage : <b className="text-[#0A1128]">{p.created_at.slice(0, 10)}</b></span>}
                  {p.deadline && <span>Deadline : <b className="text-[#0A1128]">{p.deadline.slice(0, 10)}</b></span>}
                </div>
                <div className="mt-3 text-xs font-bold text-[#06B6D4] hover:underline inline-flex items-center gap-1">
                  Ouvrir le détail →
                </div>
              </button>
            );
          })}
        </div>
      )}
      {activeProject && (
        <ProjectDetailModal project={activeProject} onClose={() => setActiveProject(null)} />
      )}
    </div>
  );
}

function ProjectDetailModal({ project, onClose }) {
  const tasks = project.tasks || [];
  const docs = project.documents || [];
  const pct = Number(project.progress_pct || 0);
  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="portal-project-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#06B6D4]">Projet · {project._company || ""}</div>
            <h3 className="display font-extrabold text-[#0A1128] text-xl truncate">{project.title}</h3>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]" data-testid="portal-project-close"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Progress hero */}
          <div className="bg-gradient-to-br from-[#0A1128] to-[#1e293b] text-white rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#67E8F9]">Avancement global</span>
              <span className="text-3xl font-extrabold tabular-nums">{pct}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#06B6D4] to-[#67E8F9] transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-3 text-xs text-white/70">{project.tasks_done || 0} étape(s) sur {project.tasks_total || 0} validée(s){project.deadline ? ` · Deadline ${project.deadline.slice(0, 10)}` : ""}</div>
          </div>

          {/* Brief */}
          {(project.description || project.brief) && (
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2 flex items-center gap-1.5"><FileText size={12} />Brief du projet</div>
              <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-4 text-sm text-[#475569] whitespace-pre-wrap leading-relaxed">{project.brief || project.description}</div>
            </div>
          )}

          {/* Tasks */}
          <div>
            <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2 flex items-center gap-1.5"><CheckCircle2 size={12} />Tâches ({tasks.length})</div>
            {tasks.length === 0 ? (
              <div className="bg-[#FAFAFA] border border-dashed border-black/15 rounded-xl p-5 text-center text-sm text-[#64748B]">Aucune tâche définie pour le moment.</div>
            ) : (
              <ul className="space-y-1.5" data-testid="portal-project-tasks">
                {tasks.map((t, i) => (
                  <li key={t.id || i} className={`flex items-start gap-2.5 p-3 rounded-xl border ${t.done ? "bg-[#A7F3D0]/15 border-[#A7F3D0]" : "bg-white border-black/8"}`}>
                    <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${t.done ? "bg-green-600" : "border-2 border-[#cbd5e1]"}`}>
                      {t.done && <CheckCircle2 size={10} className="text-white" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${t.done ? "text-[#065F46] line-through" : "text-[#0A1128]"}`}>{t.title || t.label}</div>
                      {t.done_at && <div className="text-[10px] text-[#94a3b8] mt-0.5">Validée le {t.done_at.slice(0, 10)}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Documents */}
          {docs.length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2 flex items-center gap-1.5"><FolderUp size={12} />Documents liés ({docs.length})</div>
              <ul className="space-y-1.5">
                {docs.map((d, i) => (
                  <li key={d.id || i} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-black/8 bg-white">
                    <FileText size={14} className="text-[#06B6D4]" />
                    <span className="text-sm text-[#0A1128] flex-1 truncate">{d.filename || d.title || "Document"}</span>
                    <span className="text-[10px] text-[#64748B]">{(d.created_at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Section: Documents (all companies) — grid with thumbnails ===== */
function DocumentsAllSection({ companies }) {
  const [byCompany, setByCompany] = useState({}); // {companyId: docs[]}
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | companyId
  const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'pdf' | 'image'

  useEffect(() => {
    if (companies.length === 0) { setLoading(false); return; }
    setLoading(true);
    Promise.all(companies.map((c) => api.get(`/portal/companies/${c.id}/documents`).then((r) => [c.id, r.data || []]).catch(() => [c.id, []])))
      .then((entries) => setByCompany(Object.fromEntries(entries)))
      .finally(() => setLoading(false));
  }, [companies]);

  const refresh = (cid) => {
    api.get(`/portal/companies/${cid}/documents`).then((r) => setByCompany((m) => ({ ...m, [cid]: r.data || [] }))).catch(() => {});
  };

  const allDocs = useMemo(() => {
    const arr = [];
    companies.forEach((c) => {
      (byCompany[c.id] || []).forEach((d) => arr.push({ ...d, _company: c }));
    });
    arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [byCompany, companies]);

  const filtered = allDocs.filter((d) => {
    if (filter !== "all" && d._company?.id !== filter) return false;
    if (typeFilter === "pdf"   && !String(d.mime_type || "").includes("pdf")) return false;
    if (typeFilter === "image" && !String(d.mime_type || "").startsWith("image/")) return false;
    return true;
  });

  if (companies.length === 0) {
    return <div className="bg-white rounded-3xl p-10 text-center text-sm text-[#64748B]" data-testid="portal-section-documents">Ajoutez d'abord une entreprise pour partager des documents.</div>;
  }

  return (
    <div className="space-y-3" data-testid="portal-section-documents">
      <div>
        <h2 className="display text-2xl font-extrabold text-[#0A1128]">Documents & médias partagés</h2>
        <p className="text-sm text-[#64748B]">Vignettes des PDF, PNG, JPEG échangés avec BESSON, classés par entreprise.</p>
      </div>

      {/* Filters + per-company upload */}
      <div className="bg-white rounded-2xl border border-black/8 p-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter("all")} data-testid="docs-filter-all" className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${filter === "all" ? "bg-[#0A1128] text-white" : "bg-[#FAFAFA] text-[#64748B] hover:text-[#0A1128]"}`}>
          Toutes ({allDocs.length})
        </button>
        {companies.map((c) => {
          const n = (byCompany[c.id] || []).length;
          return (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              data-testid={`docs-filter-${c.id}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${filter === c.id ? "bg-[#0A1128] text-white" : "bg-[#FAFAFA] text-[#64748B] hover:text-[#0A1128]"}`}
            >
              {c.logo_url ? <img src={c.logo_url} alt="" className="w-4 h-4 rounded-full object-cover" /> : <span className="w-4 h-4 rounded-full bg-[#06B6D4] text-[#0A1128] text-[8px] font-extrabold inline-flex items-center justify-center">{(c.name || "?").slice(0, 1).toUpperCase()}</span>}
              {c.name} ({n})
            </button>
          );
        })}
        <span className="w-px h-5 bg-black/10 mx-1" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="docs-filter-type" className="text-xs border border-black/10 rounded-full px-3 py-1.5 bg-white font-bold text-[#64748B]">
          <option value="all">Tous formats</option>
          <option value="pdf">PDF seulement</option>
          <option value="image">Images seulement</option>
        </select>
      </div>

      {/* Per-company upload zone (visible when filter is a specific company) */}
      {filter !== "all" && (
        <UploadZone companyId={filter} onUploaded={() => refresh(filter)} />
      )}

      {/* Grid */}
      {loading ? (
        <div className="bg-white rounded-3xl p-10 text-center text-sm text-[#64748B]"><Loader2 className="animate-spin inline mr-2" size={14} />Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center text-sm text-[#64748B] border border-dashed border-black/15">Aucun document à afficher.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5" data-testid="docs-grid">
          {filtered.map((d) => <DocumentCard key={d.id} doc={d} onChanged={() => refresh(d._company.id)} />)}
        </div>
      )}
    </div>
  );
}

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};
const extOf = (mime, filename) => {
  if (filename && filename.includes(".")) return filename.split(".").pop().toUpperCase();
  if (!mime) return "?";
  if (mime.includes("pdf")) return "PDF";
  if (mime.includes("png")) return "PNG";
  if (mime.includes("jpeg") || mime.includes("jpg")) return "JPG";
  return mime.split("/").pop().toUpperCase();
};

function DocumentCard({ doc, onChanged }) {
  const isImage = String(doc.mime_type || "").startsWith("image/");
  const isPdf = String(doc.mime_type || "").includes("pdf");
  const [thumb, setThumb] = useState(null);

  // Fetch a small thumbnail (private bytes) only for images
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    const url = `${window.location.origin}/api/portal/documents/${doc.id}/download`;
    const token = localStorage.getItem("besson_portal_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : null)
      .then((blob) => { if (blob && !cancelled) setThumb(URL.createObjectURL(blob)); })
      .catch(() => {});
    return () => { cancelled = true; if (thumb) URL.revokeObjectURL(thumb); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  const download = async () => {
    try {
      const url = `${window.location.origin}/api/portal/documents/${doc.id}/download`;
      const token = localStorage.getItem("besson_portal_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = doc.filename; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch { toast.error("Erreur"); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer "${doc.filename}" ?`)) return;
    try { await api.delete(`/portal/documents/${doc.id}`); toast.success("Document supprimé"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid={`doc-card-${doc.id}`} className="group bg-white rounded-2xl border border-black/8 overflow-hidden hover:shadow-md transition-all hover:border-[#06B6D4]/40">
      {/* Thumbnail area */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-[#FAFAFA] to-[#F0F4FA] flex items-center justify-center overflow-hidden">
        {isImage && thumb ? (
          <img src={thumb} alt={doc.filename} className="w-full h-full object-cover" />
        ) : isPdf ? (
          <object
            data={`${window.location.origin}/api/portal/documents/${doc.id}/download#toolbar=0&navpanes=0&view=Fit`}
            type="application/pdf"
            className="w-full h-full pointer-events-none"
            aria-label={doc.filename}
          >
            <div className="text-center p-3">
              <FileIcon size={32} className="text-[#06B6D4] mx-auto" />
              <div className="text-[10px] text-[#64748B] mt-1">{extOf(doc.mime_type, doc.filename)}</div>
            </div>
          </object>
        ) : (
          <div className="text-center p-3">
            {isImage ? <FileImage size={32} className="text-[#06B6D4] mx-auto" /> : <FileIcon size={32} className="text-[#06B6D4] mx-auto" />}
            <div className="text-[10px] text-[#64748B] mt-1">{extOf(doc.mime_type, doc.filename)}</div>
          </div>
        )}
        {/* Format badge */}
        <span className="absolute top-2 left-2 text-[9px] font-extrabold tracking-widest uppercase bg-[#0A1128] text-white px-1.5 py-0.5 rounded">
          {extOf(doc.mime_type, doc.filename)}
        </span>
        {/* Uploader badge */}
        <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: doc.uploaded_by === "admin" ? "#06B6D422" : "#A7F3D0", color: doc.uploaded_by === "admin" ? "#06B6D4" : "#065F46" }}>
          {doc.uploaded_by === "admin" ? "BESSON" : "Vous"}
        </span>
        {/* Hover actions */}
        <div className="absolute inset-x-2 bottom-2 flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={download} title="Télécharger" className="bg-white/95 backdrop-blur p-1.5 rounded-lg text-[#06B6D4] hover:bg-white shadow"><Download size={14} /></button>
          {doc.uploaded_by === "client" && (
            <button onClick={remove} title="Supprimer" className="bg-white/95 backdrop-blur p-1.5 rounded-lg text-red-500 hover:bg-white shadow"><Trash2 size={14} /></button>
          )}
        </div>
      </div>
      {/* Info */}
      <div className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] text-[#06B6D4] font-bold tracking-widest uppercase mb-1">
          {doc._company?.logo_url ? <img src={doc._company.logo_url} alt="" className="w-3 h-3 rounded-full object-cover" /> : <Building2 size={10} />}
          <span className="truncate">{doc._company?.name}</span>
        </div>
        <div className="text-sm font-bold text-[#0A1128] truncate" title={doc.filename}>{doc.filename}</div>
        <div className="text-[11px] text-[#64748B] mt-1 flex items-center justify-between">
          <span>{fmtBytes(doc.size)}</span>
          <span>{(doc.created_at || "").slice(0, 10)}</span>
        </div>
      </div>
    </div>
  );
}

function UploadZone({ companyId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("PDF, PNG, JPEG uniquement"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 Mo"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",", 2)[1] || "");
        r.onerror = rej; r.readAsDataURL(file);
      });
      await api.post(`/portal/companies/${companyId}/documents`, { filename: file.name, mime_type: file.type, data_base64: b64 });
      toast.success("Document envoyé");
      onUploaded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
      data-testid="docs-upload-zone"
      className={`bg-white rounded-2xl border-2 border-dashed p-5 text-center transition-colors ${dragOver ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-black/15"}`}
    >
      <FolderUp className="mx-auto text-[#06B6D4]" size={24} />
      <p className="text-sm text-[#0A1128] mt-2 font-bold">Glissez-déposez un fichier ici pour cette entreprise</p>
      <p className="text-[11px] text-[#64748B]">PDF, PNG, JPEG · max 5 Mo</p>
      <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1128] hover:bg-[#14213d] text-white text-xs font-bold cursor-pointer transition-colors">
        {uploading ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
        {uploading ? "Envoi…" : "Choisir un fichier"}
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden" disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      </label>
    </div>
  );
}

/* ===== Section: Appointments / Proposals / Invoices (aggregated) ===== */
function AppointmentsSection({ items }) {
  const upcoming = items.filter((a) => a.status !== "annule").sort((a, b) => (a.preferred_date + (a.preferred_time || "")).localeCompare(b.preferred_date + (b.preferred_time || "")));
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/appointment-types").then((r) => setTypes(r.data || [])).catch(() => {}); }, []);
  const ICONS = { visio: Calendar, phone: Calendar, physical: Calendar };
  const paid = types.find((t) => t.requires_payment) || types.find((t) => (t.price_eur || 0) > 0);
  const free = types.filter((t) => !t.requires_payment && (t.price_eur || 0) === 0);

  return (
    <div className="space-y-4" data-testid="portal-section-appointments">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="display text-2xl font-extrabold text-[#0A1128]">Mes rendez-vous</h2>
      </div>

      {/* CTA Premium (premier type payant) */}
      {paid && (
        <div className="bg-gradient-to-br from-[#0A1128] to-[#14213d] text-white rounded-3xl p-5 sm:p-7 relative overflow-hidden" data-testid="portal-book-premium">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#06B6D4]/20 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-[#67E8F9]/10 blur-2xl" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#67E8F9]">Consultation Premium</div>
              <div className="display text-2xl sm:text-3xl font-extrabold mt-1.5">{paid.label}</div>
              <p className="text-white/75 text-sm mt-2 max-w-md leading-relaxed">{paid.description}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-3xl font-extrabold">{paid.price_eur} €</span>
                <span className="text-xs text-white/70">TTC · paiement sécurisé Stripe · {paid.duration_minutes} min</span>
              </div>
            </div>
            <a href={`/rendez-vous?type=${paid.code}&client=1`} data-testid="portal-book-premium-btn" className="shrink-0 inline-flex items-center gap-2 bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold px-5 py-3 rounded-full transition-colors">
              <Calendar size={16} />Réserver maintenant
            </a>
          </div>
        </div>
      )}

      {/* Free options dynamiques */}
      {free.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {free.map((t) => (
            <a key={t.code} href={`/rendez-vous?type=${t.code}&client=1`} data-testid={`portal-book-${t.code}`} className="bg-white rounded-2xl border border-black/8 p-4 hover:border-[#06B6D4] transition-colors group">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#06B6D4]">Gratuit</div>
                  <div className="display font-bold text-[#0A1128] mt-0.5 truncate">{t.label}</div>
                  <div className="text-xs text-[#64748B] mt-1 line-clamp-2">{t.description}</div>
                </div>
                <Calendar size={20} className="text-[#06B6D4] group-hover:rotate-12 transition-transform shrink-0 ml-2" />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Existing appointments */}
      {upcoming.length === 0 ? (
        <div className="bg-white rounded-3xl p-8 text-center text-sm text-[#64748B] border border-dashed border-black/15">Aucun rendez-vous planifié pour le moment.</div>
      ) : (
        <div className="bg-white rounded-3xl border border-black/8 divide-y divide-black/5">
          <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Vos rendez-vous</div>
          {upcoming.map((a) => {
            const apptType = types.find((t) => t.code === a.type);
            const isPaid = apptType?.requires_payment;
            return (
              <div key={a.id} className="p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`portal-appt-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPaid ? "bg-[#FFDCA8]" : "bg-[#06B6D4]/15"}`}>
                    <Calendar size={16} className={isPaid ? "text-[#92400E]" : "text-[#06B6D4]"} />
                  </div>
                  <div>
                    <div className="display font-bold text-[#0A1128]">{apptType?.label || a.type}{apptType?.price_eur > 0 ? ` (${apptType.price_eur} €)` : ""}</div>
                    <div className="text-xs text-[#64748B]">{a._company} · {a.preferred_date} {a.preferred_time}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isPaid && a.paid && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Payé</span>}
                  {isPaid && !a.paid && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">À régler</span>}
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">{a.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProposalsSection({ items }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active && items.length > 0) setActive(items[0]);
    if (active && !items.find((p) => p.id === active.id)) setActive(items[0] || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const downloadPdf = (p) => {
    if (!p.public_token) return toast.error("PDF indisponible");
    window.open(`${window.location.origin}/api/proposals/public/${p.public_token}/pdf`, "_blank");
  };

  const statusOf = (p) => {
    if (p.status === "signe" || p.status === "acompte") return { label: "Signé · acompte versé", tone: "good" };
    if (p.status === "solde") return { label: "Soldé", tone: "good" };
    if (p.status === "signe") return { label: "Signé", tone: "good" };
    if (p.status === "envoye" && p.opened_at) return { label: "Consulté", tone: "ok" };
    if (p.status === "envoye") return { label: "En attente", tone: "warn" };
    if (p.status === "brouillon") return { label: "Brouillon", tone: "neutral" };
    if (p.status === "refuse") return { label: "Refusé", tone: "cold" };
    return { label: p.status || "—", tone: "neutral" };
  };
  const tone = {
    good:    { bg: "#A7F3D044", text: "#065F46" },
    ok:      { bg: "#06B6D41A", text: "#06B6D4" },
    warn:    { bg: "#FFDCA866", text: "#92400E" },
    cold:    { bg: "#FEE2E2",  text: "#B91C1C" },
    neutral: { bg: "#F1F5F9",  text: "#64748B" },
  };

  if (items.length === 0) {
    return (
      <div className="space-y-4" data-testid="portal-section-proposals">
        <h2 className="display text-2xl font-extrabold text-[#0A1128]">Devis & propositions</h2>
        <div className="bg-white rounded-3xl p-10 text-center text-sm text-[#64748B] border border-dashed border-black/15">Aucun devis pour l'instant.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-section-proposals">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="display text-2xl font-extrabold text-[#0A1128]">Devis & propositions</h2>
        {active && active.public_token && (
          <Button onClick={() => downloadPdf(active)} className="bg-[#0A1128] text-white hover:bg-[#14213d]" data-testid="portal-proposal-download">
            <Download size={14} className="mr-1.5" />Télécharger le PDF
          </Button>
        )}
      </div>

      {/* Split view: table left, PDF preview right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-black/8 overflow-hidden lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto">
          <table className="w-full text-sm" data-testid="portal-proposals-table">
            <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5">Devis</th>
                <th className="text-right px-3 py-2.5">TTC</th>
                <th className="text-left px-3 py-2.5">État</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const st = statusOf(p);
                const c = tone[st.tone];
                const isActive = active?.id === p.id;
                return (
                  <tr key={p.id} onClick={() => setActive(p)} data-testid={`portal-proposal-row-${p.id}`} className={`border-b border-black/5 last:border-0 cursor-pointer transition-colors ${isActive ? "bg-[#06B6D4]/8" : "hover:bg-[#FAFAFA]"}`}>
                    <td className="px-3 py-3">
                      <div className="display font-extrabold text-[#0A1128]">{p.ref}</div>
                      <div className="text-[11px] text-[#64748B] truncate max-w-[180px]">{p.project?.title || p._company || "—"}</div>
                      <div className="text-[10px] text-[#94a3b8]">{(p.created_at || "").slice(0, 10)}</div>
                    </td>
                    <td className="px-3 py-3 text-right font-extrabold tabular-nums text-[#0A1128]">{Number((p.totals?.total_ttc) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €</td>
                    <td className="px-3 py-3">
                      <span data-testid={`portal-proposal-status-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ background: c.bg, color: c.text }}>
                        {st.tone === "ok" && <Eye size={10} />}
                        {st.tone === "good" && <CheckCircle2 size={10} />}
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PDF preview of active row */}
        <div className="bg-white rounded-2xl border border-black/8 overflow-hidden lg:sticky lg:top-4 lg:max-h-[calc(100vh-200px)]" data-testid="portal-proposal-preview">
          {active && active.public_token ? (
            <>
              <div className="px-4 py-2.5 border-b border-black/8 bg-[#FAFAFA] flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="display font-extrabold text-[#0A1128] text-sm truncate">{active.ref}</div>
                  <div className="text-[11px] text-[#64748B] truncate">{active.project?.title}</div>
                </div>
                <a href={`${window.location.origin}/api/proposals/public/${active.public_token}/pdf`} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#06B6D4] hover:underline inline-flex items-center gap-1 flex-shrink-0">
                  Plein écran <ExternalLink size={11} />
                </a>
              </div>
              <object
                data={`${window.location.origin}/api/proposals/public/${active.public_token}/pdf?v=${active.updated_at || active.id}#toolbar=0&navpanes=0`}
                type="application/pdf"
                className="w-full"
                style={{ height: "calc(100vh - 280px)", minHeight: 480 }}
              >
                <iframe title={active.ref} src={`${window.location.origin}/api/proposals/public/${active.public_token}/pdf?v=${active.updated_at || active.id}`} className="w-full" style={{ height: "calc(100vh - 280px)", minHeight: 480 }} />
              </object>
            </>
          ) : (
            <div className="p-8 text-center text-sm text-[#64748B]">Sélectionnez un devis pour afficher l'aperçu.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoicesSection({ items }) {
  return (
    <div className="space-y-4" data-testid="portal-section-invoices">
      <h2 className="display text-2xl font-extrabold text-[#0A1128]">Mes factures</h2>
      {items.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center text-sm text-[#64748B] border border-dashed border-black/15">Aucune facture pour l'instant.</div>
      ) : (
        <div className="bg-white rounded-3xl border border-black/8 divide-y divide-black/5">
          {items.map((i) => (
            <div key={i.id} className="p-5 flex items-center justify-between flex-wrap gap-2" data-testid={`portal-invoice-${i.id}`}>
              <div>
                <div className="display font-bold text-[#0A1128]">{i.ref}</div>
                <div className="text-xs text-[#64748B]">{i._company} · {(i.amount_ttc || 0).toFixed(2)} € TTC</div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${i.paid ? "bg-[#A7F3D0] text-[#065F46]" : "bg-[#FFDCA8] text-[#92400E]"}`}>{i.paid ? "Payée" : (i.status || "À régler")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===== Section: Account ===== */
function AccountSection({ me, onUpdated }) {
  const [form, setForm] = useState({
    full_name: me?.full_name || "",
    phone: me?.phone || "",
    role_title: me?.role_title || "",
    bio: me?.bio || "",
    avatar_url: me?.avatar_url || "",
    preferred_language: me?.preferred_language || "fr",
    notify_proposals: me?.notify_proposals ?? true,
    notify_invoices: me?.notify_invoices ?? true,
    notify_appointments: me?.notify_appointments ?? true,
    notify_project_updates: me?.notify_project_updates ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (me) setForm((f) => ({
      ...f,
      full_name: me.full_name || "",
      phone: me.phone || "",
      role_title: me.role_title || "",
      bio: me.bio || "",
      avatar_url: me.avatar_url || "",
      preferred_language: me.preferred_language || "fr",
      notify_proposals: me.notify_proposals ?? true,
      notify_invoices: me.notify_invoices ?? true,
      notify_appointments: me.notify_appointments ?? true,
      notify_project_updates: me.notify_project_updates ?? true,
    }));
  }, [me]);

  useEffect(() => {
    api.get("/portal/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/portal/me", form);
      onUpdated && onUpdated(r.data);
      toast.success("Profil mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Fichier > 2 MB");
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        const next = { ...form, avatar_url: dataUrl };
        setForm(next);
        const r = await api.patch("/portal/me", { avatar_url: dataUrl });
        onUpdated && onUpdated(r.data);
        toast.success("Photo mise à jour");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error("Erreur"); setUploading(false); }
  };

  const removeAvatar = async () => {
    setForm({ ...form, avatar_url: "" });
    try {
      const r = await api.patch("/portal/me", { avatar_url: "" });
      onUpdated && onUpdated(r.data);
      toast.success("Photo supprimée");
    } catch { toast.error("Erreur"); }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) return toast.error("Tous les champs requis");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Les nouveaux mots de passe ne correspondent pas");
    if (pwForm.new_password.length < 8) return toast.error("Au moins 8 caractères");
    setPwBusy(true);
    try {
      await api.post("/portal/me/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Mot de passe modifié");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setPwBusy(false); }
  };

  const initials = (form.full_name || me?.email || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-4" data-testid="portal-section-account">
      <h2 className="display text-2xl font-extrabold text-[#0A1128]">Mon compte</h2>

      {/* Identity card */}
      <div className="bg-gradient-to-br from-[#0A1128] to-[#1e293b] text-white rounded-3xl p-6 shadow-md">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="relative flex-shrink-0">
            {form.avatar_url ? (
              <img src={form.avatar_url} alt="" className="w-24 h-24 rounded-2xl object-cover ring-4 ring-white/10" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#06B6D4] to-[#67E8F9] flex items-center justify-center text-[#0A1128] font-extrabold text-3xl">{initials}</div>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="account-avatar-upload"
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#06B6D4] text-[#0A1128] flex items-center justify-center shadow-md hover:bg-[#67E8F9] transition-colors"
              title="Changer la photo"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            </button>
            <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={uploadAvatar} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#67E8F9]">Profil entrepreneur</div>
            <div className="display text-2xl font-extrabold mt-0.5">{form.full_name || "—"}</div>
            {form.role_title && <div className="text-sm text-white/70 mt-0.5">{form.role_title}</div>}
            <div className="text-xs text-white/60 mt-1">{me?.email}</div>
            {form.avatar_url && (
              <button onClick={removeAvatar} className="mt-2 text-[10px] uppercase tracking-widest text-white/60 hover:text-white">Supprimer la photo</button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="account-stats">
          <StatTile color="#06B6D4" icon={Building2} value={stats.companies_count} label="Entreprises" />
          <StatTile color="#A7F3D0" icon={FileSignature} value={stats.proposals_signed} label="Devis signés" />
          <StatTile color="#FFDCA8" icon={FileText} value={`${stats.projects_delivered}/${stats.projects_total}`} label="Projets livrés" />
          <StatTile color="#67E8F9" icon={Receipt} value={stats.invoices_paid} label="Factures payées" />
        </div>
      )}
      {stats?.member_since && (
        <div className="text-xs text-[#64748B] text-center">Membre depuis le <b className="text-[#0A1128]">{stats.member_since.slice(0, 10)}</b></div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Personal info */}
        <div className="bg-white rounded-3xl p-5 border border-black/8 space-y-3">
          <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Informations personnelles</div>
          <div><Label className="text-xs">Email <span className="text-[#94a3b8]">(non modifiable)</span></Label><Input value={me?.email || ""} disabled /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Prénom</Label><Input data-testid="account-first-name" value={form.first_name || ""} onChange={(e) => { const fn = e.target.value; setForm({ ...form, first_name: fn, full_name: `${fn} ${form.last_name || ""}`.trim() }); }} /></div>
            <div><Label className="text-xs">Nom</Label><Input data-testid="account-last-name" value={form.last_name || ""} onChange={(e) => { const ln = e.target.value; setForm({ ...form, last_name: ln, full_name: `${form.first_name || ""} ${ln}`.trim() }); }} /></div>
          </div>
          <div><Label className="text-xs">Fonction / Rôle</Label><Input data-testid="account-role" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Ex : Fondatrice, Directeur commercial" /></div>
          <div><Label className="text-xs">Téléphone</Label><Input data-testid="account-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label className="text-xs">Bio courte</Label><Textarea data-testid="account-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Quelques mots sur votre activité..." /></div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-3xl p-5 border border-black/8 space-y-3">
          <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Préférences</div>
          <div>
            <Label className="text-xs">Langue d'affichage</Label>
            <select data-testid="account-lang" value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white">
              <option value="fr">Français</option>
              <option value="en">English (bientôt)</option>
            </select>
          </div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] pt-1">Notifications par email</div>
          <NotifToggle label="Nouveaux devis & relances" testId="notif-proposals" checked={!!form.notify_proposals} onChange={(v) => setForm({ ...form, notify_proposals: v })} />
          <NotifToggle label="Factures & paiements" testId="notif-invoices" checked={!!form.notify_invoices} onChange={(v) => setForm({ ...form, notify_invoices: v })} />
          <NotifToggle label="Rendez-vous" testId="notif-appointments" checked={!!form.notify_appointments} onChange={(v) => setForm({ ...form, notify_appointments: v })} />
          <NotifToggle label="Avancement projets" testId="notif-projects" checked={!!form.notify_project_updates} onChange={(v) => setForm({ ...form, notify_project_updates: v })} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold" data-testid="account-save">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14} />Enregistrement…</> : "Enregistrer les modifications"}
        </Button>
      </div>

      {/* Security */}
      <div className="bg-white rounded-3xl p-5 border border-black/8 space-y-3" data-testid="account-security">
        <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Sécurité — Changer mon mot de passe</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Mot de passe actuel</Label><Input type="password" data-testid="account-pw-current" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} /></div>
          <div><Label className="text-xs">Nouveau mot de passe</Label><Input type="password" data-testid="account-pw-new" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="8 caractères minimum" /></div>
          <div><Label className="text-xs">Confirmer le nouveau</Label><Input type="password" data-testid="account-pw-confirm" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={changePassword} disabled={pwBusy} variant="outline" data-testid="account-pw-save">
            {pwBusy ? <><Loader2 className="animate-spin mr-2" size={14} />Modification…</> : "Modifier le mot de passe"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatTile({ color, icon: Icon, value, label }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: color }}>
        <Icon size={16} className="text-[#0A1128]" />
      </div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-2 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mt-0.5">{label}</div>
    </div>
  );
}

function NotifToggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        data-testid={testId}
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-[#06B6D4]" : "bg-slate-300"}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </button>
      <span className="text-sm text-[#0A1128]">{label}</span>
    </label>
  );
}

/* -------- Detail panel -------- */
function CompanyDetail({ companyId, onEdit, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/portal/companies/${companyId}`).then((r) => setData(r.data)).catch(() => toast.error("Erreur")).finally(() => setLoading(false));
  }, [companyId]);

  if (loading || !data) return <div className="bg-white rounded-2xl p-6 text-center text-sm text-[#64748B]"><Loader2 className="animate-spin inline mr-2" />Chargement…</div>;

  const c = data.company;

  const deleteCompany = async () => {
    if (!window.confirm(`Supprimer la carte de "${c.name}" ? L'historique des prestations reste accessible côté admin.`)) return;
    try { await api.delete(`/portal/companies/${c.id}`); toast.success("Carte supprimée"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid={`company-detail-${c.id}`}>
      {/* Header */}
      <div className="bg-white rounded-3xl p-6 border border-black/8">
        <div className="flex items-start gap-4 flex-wrap">
          {c.logo_url ? (
            <img src={c.logo_url} alt={c.name} className="w-20 h-20 rounded-2xl object-cover shadow-md" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#0A1128] to-[#14213d] flex items-center justify-center text-white font-extrabold text-3xl">{(c.name || "?").slice(0, 1).toUpperCase()}</div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="display text-2xl font-extrabold text-[#0A1128]">{c.name}</h2>
            <div className="text-sm text-[#64748B]">{c.sector || "Secteur non renseigné"}</div>
            {c.description && <p className="text-sm text-[#475569] mt-2 max-w-xl">{c.description}</p>}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid="company-edit"><Edit3 size={14} /></Button>
            <Button size="sm" variant="outline" onClick={deleteCompany} data-testid="company-delete" className="text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
          </div>
        </div>

        {/* Coordinates grid */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {c.website && <Info label="Site web" value={<a href={c.website} target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline inline-flex items-center gap-1">{c.website} <ExternalLink size={12} /></a>} />}
          {c.email && <Info label="Email" value={c.email} />}
          {c.phone && <Info label="Téléphone" value={c.phone} />}
          {c.address && <Info label="Adresse" value={c.address} />}
          {c.siret && <Info label="SIRET" value={c.siret} />}
          {c.tva && <Info label="N° TVA" value={c.tva} />}
        </div>
      </div>

      {/* Accesses (encrypted) */}
      {(c.accesses || []).length > 0 && (
        <div className="bg-white rounded-3xl p-6 border border-black/8" data-testid="company-accesses">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound size={18} className="text-[#06B6D4]" />
            <h3 className="display font-extrabold text-[#0A1128]">Accès & identifiants</h3>
            <span className="text-[10px] text-[#64748B]">Chiffrés côté serveur, visibles uniquement par vous</span>
          </div>
          <div className="space-y-2">
            {c.accesses.map((a, i) => <AccessRow key={i} access={a} />)}
          </div>
        </div>
      )}

      {/* Stats / history */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Stat icon={FileSignature} label="Devis" value={data.proposals.length} color="#06B6D4" />
        <Stat icon={Receipt} label="Factures" value={data.invoices.length} color="#A7F3D0" />
        <Stat icon={Calendar} label="RDV" value={data.appointments.length} color="#67E8F9" />
        <Stat icon={FileText} label="Projets" value={(data.projects || []).length} color="#FFDCA8" />
      </div>

      {/* Projects with animated progress bar */}
      {(data.projects || []).length > 0 && (
        <div className="bg-white rounded-3xl p-6 border border-black/8" data-testid="company-projects">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-[#06B6D4]" />
            <h3 className="display font-extrabold text-[#0A1128]">Projets en cours</h3>
            <span className="text-[10px] text-[#64748B]">Suivi en temps réel par BESSON</span>
          </div>
          <div className="space-y-4">
            {(data.projects || []).map((p) => <ProjectProgress key={p.id} project={p} />)}
          </div>
        </div>
      )}

      {/* Lists */}
      <ListBlock title="Devis & propositions" items={data.proposals} icon={FileSignature} render={(p) => (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="display font-bold text-[#0A1128]">{p.ref}</div>
            <div className="text-xs text-[#64748B]">{p.project?.title || "—"} · {(p.totals?.total_ttc || 0).toFixed(2)} € TTC</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p.status === "signe" || p.status === "acompte" || p.status === "solde" ? "bg-[#A7F3D0] text-[#065F46]" : "bg-[#FFDCA8] text-[#92400E]"}`}>{p.status}</span>
            {p.public_token && <a href={`/devis/${p.public_token}`} target="_blank" rel="noreferrer" className="text-[#06B6D4] text-xs font-bold hover:underline">Voir</a>}
          </div>
        </div>
      )} />

      <ListBlock title="Rendez-vous" items={data.appointments} icon={Calendar} render={(a) => (
        <div>
          <div className="display font-bold text-[#0A1128]">{a.type === "visio_1h" ? "Visio 1h" : a.type === "phone_15m" ? "Téléphone 15 min" : "Physique"}</div>
          <div className="text-xs text-[#64748B]">{a.preferred_date} {a.preferred_time} · {a.status}</div>
        </div>
      )} />

      {/* Shared documents (admin ↔ client) */}
      <SharedDocuments companyId={c.id} />
    </div>
  );
}

function SharedDocuments({ companyId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/portal/companies/${companyId}/documents`)
      .then((r) => setDocs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
  const MAX_BYTES = 5 * 1024 * 1024;

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result || "";
      const b64 = String(result).split(",", 2)[1] || "";
      resolve(b64);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Format non autorisé. PDF, PNG, JPEG uniquement."); return; }
    if (file.size > MAX_BYTES) { toast.error("Fichier trop volumineux (max 5 Mo)."); return; }
    setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/portal/companies/${companyId}/documents`, {
        filename: file.name, mime_type: file.type, data_base64: b64,
      });
      toast.success("Document envoyé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const downloadDoc = async (d) => {
    try {
      const url = `${window.location.origin}/api/portal/documents/${d.id}/download`;
      const token = localStorage.getItem("besson_portal_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Téléchargement impossible");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = d.filename; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) { toast.error("Erreur de téléchargement"); }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Supprimer "${d.filename}" ?`)) return;
    try { await api.delete(`/portal/documents/${d.id}`); toast.success("Document supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression refusée"); }
  };

  return (
    <div data-testid="shared-documents" className="bg-white rounded-3xl p-6 border border-black/8">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <FolderUp size={18} className="text-[#06B6D4]" />
          <h3 className="display font-extrabold text-[#0A1128]">Documents partagés</h3>
          <span className="text-[10px] text-[#64748B]">PDF, PNG, JPEG · 5 Mo max</span>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1128] hover:bg-[#14213d] text-white text-sm font-bold transition-colors" data-testid="doc-upload-btn">
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Envoyer un document
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
            disabled={uploading}
          />
        </label>
      </div>

      <div
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="doc-dropzone"
        className={`border-2 border-dashed rounded-2xl p-6 text-center text-sm transition-colors ${dragOver ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-black/15 bg-[#FAFAFA]"} mb-4`}
      >
        {uploading ? (
          <span className="text-[#06B6D4] inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} />Envoi en cours…</span>
        ) : (
          <span className="text-[#64748B]">Glissez-déposez un fichier ici, ou cliquez sur « Envoyer un document » ci-dessus.</span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#64748B]"><Loader2 className="animate-spin inline mr-2" size={14} />Chargement…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-[#64748B] text-center py-6">Aucun document pour l'instant.</div>
      ) : (
        <ul className="divide-y divide-black/5">
          {docs.map((d) => (
            <li key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 py-3">
              <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#FAFAFA] border border-black/8 text-[#06B6D4]">
                {String(d.mime_type || "").startsWith("image/") ? <FileImage size={18} /> : <FileIcon size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#0A1128] truncate">{d.filename}</div>
                <div className="text-[11px] text-[#64748B] flex items-center gap-2 flex-wrap">
                  <span className="font-semibold uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded" style={{ background: d.uploaded_by === "admin" ? "#06B6D422" : "#A7F3D044", color: d.uploaded_by === "admin" ? "#06B6D4" : "#065F46" }}>
                    {d.uploaded_by === "admin" ? "BESSON" : "Vous"}
                  </span>
                  <span>{d.uploader_name || "—"}</span>
                  <span>· {(d.size / 1024).toFixed(1)} Ko</span>
                  <span>· {(d.created_at || "").slice(0, 10)}</span>
                </div>
                {d.note && <div className="text-[11px] text-[#475569] italic mt-0.5 truncate">« {d.note} »</div>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => downloadDoc(d)} title="Télécharger" className="p-2 rounded-lg hover:bg-[#FAFAFA] text-[#06B6D4]"><Download size={14} /></button>
                {d.uploaded_by === "client" && (
                  <button onClick={() => removeDoc(d)} title="Supprimer" className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-widest uppercase text-[#64748B]">{label}</div>
      <div className="text-[#0A1128] mt-0.5">{value}</div>
    </div>
  );
}

function ProjectProgress({ project }) {
  const [open, setOpen] = useState(false);
  const total = project.tasks_total ?? (project.tasks || []).length;
  const done = project.tasks_done ?? (project.tasks || []).filter((t) => t.done).length;
  const pct = project.progress_pct ?? (total ? Math.round((done / total) * 100) : 0);
  const [animatedPct, setAnimatedPct] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedPct(pct), 80);
    return () => clearTimeout(t);
  }, [pct]);

  const STATUS_FR = {
    decouverte: "Découverte",
    cadrage: "Cadrage",
    design: "Design",
    dev: "Développement",
    livraison: "Livraison",
    termine: "Terminé",
  };

  return (
    <div data-testid={`project-${project.id}`} className="bg-[#FAFAFA] rounded-2xl p-4 border border-black/5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="display font-extrabold text-[#0A1128] truncate">{project.title}</div>
          <div className="text-xs text-[#64748B] flex items-center gap-2 mt-0.5">
            <span className="inline-block px-2 py-0.5 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] font-bold uppercase tracking-widest text-[9px]">{STATUS_FR[project.status] || project.status}</span>
            {total > 0 && <span>{done}/{total} tâches</span>}
          </div>
        </div>
        <div className="display text-3xl font-extrabold tabular-nums text-[#06B6D4]">{pct}%</div>
      </div>

      {/* Animated progress bar */}
      <div className="mt-3 h-3 rounded-full bg-white border border-black/8 overflow-hidden relative">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#06B6D4] via-[#67E8F9] to-[#A7F3D0] transition-all duration-1000 ease-out relative overflow-hidden"
          style={{ width: `${animatedPct}%` }}
        >
          <div className="absolute inset-0 opacity-50" style={{
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
            backgroundSize: "200% 100%",
            animation: "shimmer 2.5s linear infinite",
          }} />
        </div>
      </div>

      {total > 0 && (
        <>
          <button onClick={() => setOpen(!open)} className="mt-3 text-xs font-bold text-[#06B6D4] hover:underline inline-flex items-center gap-1" data-testid={`project-toggle-${project.id}`}>
            <ListChecks size={12} /> {open ? "Masquer le détail" : "Voir le détail des tâches"}
          </button>
          {open && (
            <ul className="mt-2 space-y-1.5" data-testid={`project-tasks-${project.id}`}>
              {(project.tasks || []).map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-sm bg-white border border-black/5 rounded-lg px-3 py-1.5">
                  <CheckCircle2 size={15} className={t.done ? "text-[#06B6D4]" : "text-[#cbd5e1]"} />
                  <span className={t.done ? "line-through text-[#64748B]" : "text-[#0A1128]"}>{t.label}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-black/8">
      <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase" style={{ color }}>
        <Icon size={12} /> {label}
      </div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-1">{value}</div>
    </div>
  );
}

function ListBlock({ title, items, icon: Icon, render }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-3xl border border-black/8 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#FAFAFA]">
        <div className="flex items-center gap-2"><Icon size={16} className="text-[#06B6D4]" /><span className="display font-extrabold text-[#0A1128]">{title}</span><span className="text-xs text-[#64748B]">({items.length})</span></div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="border-t border-black/8 divide-y divide-black/5">
          {items.map((it, i) => <div key={it.id || i} className="px-5 py-3 hover:bg-[#FAFAFA]">{render(it)}</div>)}
        </div>
      )}
    </div>
  );
}

function AccessRow({ access }) {
  const [show, setShow] = useState(false);
  return (
    <div className="bg-[#FAFAFA] rounded-xl p-3 border border-black/5 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="display font-bold text-sm text-[#0A1128]">{access.label}</div>
        {access.url && <a href={access.url} target="_blank" rel="noreferrer" className="text-xs text-[#06B6D4] hover:underline break-all">{access.url}</a>}
        {access.notes && <div className="text-xs text-[#64748B] mt-1 italic">{access.notes}</div>}
      </div>
      <div className="text-xs text-[#475569] min-w-[150px]">
        <div>Utilisateur : <code className="bg-white px-1 py-0.5 rounded">{access.username || "—"}</code></div>
        <div className="mt-1 flex items-center gap-2">
          Mot de passe :
          <code className="bg-white px-1 py-0.5 rounded">{show ? (access.password || "—") : (access.has_password ? "•••••••••" : "—")}</code>
          {access.has_password && <button onClick={() => setShow(!show)} className="text-[#06B6D4]">{show ? <EyeOff size={12} /> : <Eye size={12} />}</button>}
        </div>
      </div>
    </div>
  );
}

/* -------- Company create/edit modal -------- */
function CompanyModal({ company, onClose, onSaved }) {
  const isEdit = Boolean(company?.id);
  const [form, setForm] = useState(() => ({
    name: company?.name || "", sector: company?.sector || "", logo_url: company?.logo_url || "",
    website: company?.website || "", siret: company?.siret || "", tva: company?.tva || "",
    address: company?.address || "", phone: company?.phone || "", email: company?.email || "",
    description: company?.description || "",
    accesses: (company?.accesses || []).map((a) => ({ ...a, password: "" })),
  }));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/portal/companies/${company.id}`, form);
      else await api.post("/portal/companies", form);
      toast.success(isEdit ? "Entreprise mise à jour" : "Entreprise ajoutée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const addAccess = () => setForm({ ...form, accesses: [...form.accesses, { label: "", url: "", username: "", password: "", notes: "" }] });
  const updateAccess = (i, k, v) => {
    const copy = [...form.accesses]; copy[i] = { ...copy[i], [k]: v }; setForm({ ...form, accesses: copy });
  };
  const removeAccess = (i) => setForm({ ...form, accesses: form.accesses.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="company-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
          <div>
            <div className="display font-extrabold text-[#0A1128] text-lg">{isEdit ? "Modifier l'entreprise" : "Ajouter une entreprise"}</div>
            <div className="text-xs text-[#64748B]">Coordonnées · Logo · Accès chiffrés</div>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-4">
          {!isEdit && <SiretLookupBlock onApply={(d) => setForm((f) => ({
            ...f,
            name: d.nom_complet || d.denomination || f.name,
            siret: d.siret || f.siret,
            address: d.adresse ? `${d.adresse}${d.code_postal ? `, ${d.code_postal}` : ""}${d.commune ? ` ${d.commune}` : ""}`.trim() : f.address,
            sector: d.section_activite || d.activite_principale || f.sector,
            website: d.site_internet || f.website,
            description: f.description || (d.dirigeants && d.dirigeants[0] ? `Dirigeant : ${d.dirigeants[0].prenoms || ""} ${d.dirigeants[0].nom || ""}`.trim() : ""),
          }))} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nom de l'entreprise *</Label><Input data-testid="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Secteur</Label><Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Ex : Restauration" /></div>
            <div><Label>Site web</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>SIRET</Label><Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
            <div><Label>N° TVA</Label><Input value={form.tva} onChange={(e) => setForm({ ...form, tva: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Logo (URL)</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://...logo.png" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>

          <div className="border-t border-black/8 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-[#0A1128] font-bold">Accès aux outils (chiffrés)</Label>
              <Button type="button" size="sm" variant="outline" onClick={addAccess} data-testid="add-access"><Plus size={12} className="mr-1" />Ajouter un accès</Button>
            </div>
            <div className="space-y-2 mt-3">
              {form.accesses.map((a, i) => (
                <div key={i} className="bg-[#FAFAFA] rounded-xl p-3 border border-black/5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input placeholder="Nom de l'accès (ex: WordPress)" value={a.label} onChange={(e) => updateAccess(i, "label", e.target.value)} />
                    <Input placeholder="URL (https://)" value={a.url || ""} onChange={(e) => updateAccess(i, "url", e.target.value)} />
                    <Input placeholder="Identifiant" value={a.username || ""} onChange={(e) => updateAccess(i, "username", e.target.value)} />
                    <Input type="password" placeholder={a.has_password ? "Laisser vide pour conserver" : "Mot de passe"} value={a.password || ""} onChange={(e) => updateAccess(i, "password", e.target.value)} />
                    <Textarea rows={1} placeholder="Notes (optionnel)" className="sm:col-span-2" value={a.notes || ""} onChange={(e) => updateAccess(i, "notes", e.target.value)} />
                  </div>
                  <div className="text-right mt-2">
                    <button type="button" onClick={() => removeAccess(i)} className="text-xs text-red-600 hover:underline"><Trash2 size={12} className="inline mr-1" />Retirer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-black/8 flex justify-end gap-2 bg-[#FAFAFA]">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="save-company" className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold">
            {busy ? <><Loader2 className="animate-spin mr-2" size={14} />Enregistrement…</> : (isEdit ? "Enregistrer" : "Créer la carte")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SiretLookupBlock({ onApply }) {
  const [mode, setMode] = useState("siret"); // siret | manual
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [related, setRelated] = useState(null);
  const [relatedBusy, setRelatedBusy] = useState(false);

  const search = async () => {
    const val = q.trim();
    if (!val) return;
    setBusy(true);
    setTouched(true);
    try {
      const cleaned = val.replace(/\s/g, "");
      const isSiret = /^\d{14}$/.test(cleaned);
      const isSiren = /^\d{9}$/.test(cleaned);
      const params = isSiret ? `siret=${cleaned}` : isSiren ? `siren=${cleaned}` : `q=${encodeURIComponent(val)}`;
      const r = await api.get(`/portal/company-lookup?${params}`);
      setResults(r.data.results || []);
      if ((r.data.results || []).length === 0) toast.message("Aucun résultat. Vous pouvez remplir manuellement.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recherche indisponible"); }
    finally { setBusy(false); }
  };

  const fetchRelated = async (dirigeant, sirenToExclude) => {
    if (!dirigeant?.nom) return;
    setRelatedBusy(true);
    setRelated({ dirigeant, items: [] });
    try {
      const r = await api.get("/portal/related-companies", { params: { nom: dirigeant.nom, prenoms: dirigeant.prenoms || "", exclude_siren: sirenToExclude || "" } });
      setRelated({ dirigeant, items: r.data.results || [] });
    } catch { setRelated({ dirigeant, items: [] }); }
    finally { setRelatedBusy(false); }
  };

  const applyResult = (r) => {
    onApply(r);
    setResults([]);
    setTouched(false);
    if (r.dirigeants && r.dirigeants[0]) fetchRelated(r.dirigeants[0], r.siren);
  };

  if (mode === "manual") {
    return (
      <div className="bg-[#FFDCA8]/20 border border-[#FFDCA8] rounded-2xl p-3 flex items-center justify-between">
        <div className="text-xs text-[#92400E]"><strong>Saisie manuelle activée</strong> · entreprise en cours de création ou non référencée</div>
        <button type="button" onClick={() => setMode("siret")} className="text-xs font-bold text-[#06B6D4] hover:underline" data-testid="company-back-to-siret">Rechercher SIRET</button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#06B6D4]/10 to-[#67E8F9]/5 border border-[#06B6D4]/20 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-full bg-[#06B6D4] text-[#0A1128] flex items-center justify-center font-extrabold text-xs">1</div>
        <div>
          <div className="display font-extrabold text-[#0A1128] text-sm">Recherche automatique de votre entreprise</div>
          <div className="text-[11px] text-[#64748B]">Tapez votre SIRET, SIREN ou nom — données pré-remplies depuis l'Annuaire des Entreprises (gouv.fr)</div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Input
          data-testid="company-siret-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder="Ex : 81234567800012  ou  Café des Lumières"
          className="flex-1"
        />
        <Button onClick={search} disabled={busy || !q.trim()} type="button" className="bg-[#0A1128] text-white hover:bg-[#14213d]" data-testid="company-siret-go">
          {busy ? <Loader2 className="animate-spin" size={14} /> : "Rechercher"}
        </Button>
      </div>
      {touched && !busy && results.length === 0 && !related && (
        <div className="mt-3 text-[12px] text-[#64748B] bg-white/60 rounded-lg p-2.5 border border-black/5">
          ⚠ Aucun résultat. Si votre entreprise est en cours de création ou non référencée,{" "}
          <button type="button" onClick={() => setMode("manual")} className="text-[#06B6D4] font-bold hover:underline" data-testid="company-go-manual">passez en saisie manuelle →</button>
        </div>
      )}
      {results.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {results.map((r) => (
            <li key={r.siren}>
              <button
                type="button"
                onClick={() => { applyResult(r); toast.success(`Informations de ${r.nom_complet || r.denomination} pré-remplies ✓`); }}
                data-testid={`company-siret-result-${r.siren}`}
                className="w-full text-left bg-white border border-black/8 rounded-xl p-3 hover:border-[#06B6D4] hover:bg-[#06B6D4]/5 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="display font-extrabold text-[#0A1128] text-sm truncate">{r.nom_complet || r.denomination}</div>
                    <div className="text-[11px] text-[#64748B] truncate">SIRET {r.siret} · {r.forme_juridique || "—"} · {r.commune || ""}</div>
                    {r.dirigeants && r.dirigeants[0] && (
                      <div className="text-[11px] text-[#06B6D4] mt-0.5">👤 {r.dirigeants[0].prenoms} {r.dirigeants[0].nom} ({r.dirigeants[0].qualite || "Dirigeant"})</div>
                    )}
                  </div>
                  <span className="shrink-0 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#06B6D4] text-[#0A1128]">Sélectionner</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Related companies suggestion */}
      {related && (
        <div className="mt-3 pt-3 border-t border-[#06B6D4]/30" data-testid="company-related">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="display font-extrabold text-[#0A1128] text-sm">✨ Vous êtes aussi dirigeant de…</div>
              <div className="text-[10px] text-[#64748B]">Mandats actifs détectés pour {related.dirigeant.prenoms} {related.dirigeant.nom} (Annuaire des Entreprises). Ajoutez celles qui vous concernent.</div>
            </div>
            <button type="button" onClick={() => setRelated(null)} className="text-[#94a3b8] hover:text-[#0A1128] p-1 text-xs">Masquer</button>
          </div>
          {relatedBusy ? (
            <div className="text-xs text-[#64748B] py-3 text-center"><Loader2 className="animate-spin inline mr-1" size={12} />Recherche en cours…</div>
          ) : related.items.length === 0 ? (
            <div className="text-xs text-[#64748B] py-2 text-center">Aucun autre mandat trouvé.</div>
          ) : (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {related.items.map((r) => (
                <li key={r.siren}>
                  <button type="button" onClick={() => { onApply({
                    nom_complet: r.nom_complet, siret: r.siret,
                    adresse: r.adresse, code_postal: r.code_postal, commune: r.commune,
                    section_activite: r.section_activite, activite_principale: r.activite_principale,
                    site_internet: r.site_internet,
                  }); setRelated((prev) => prev ? { ...prev, items: prev.items.filter((x) => x.siren !== r.siren) } : null); toast.success(`${r.nom_complet} ajoutée`); }}
                  data-testid={`company-related-${r.siren}`}
                  className="w-full text-left bg-white border border-black/8 rounded-lg p-2 hover:border-[#06B6D4] hover:bg-[#06B6D4]/5 transition-colors flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#0A1128] text-sm truncate">{r.nom_complet}</div>
                      <div className="text-[10px] text-[#64748B] truncate">SIREN {r.siren} · {r.role} · {r.commune || ""}</div>
                    </div>
                    <span className="shrink-0 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#06B6D4] text-[#0A1128]">+ Ajouter</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-black/8 text-center">
        <button type="button" onClick={() => setMode("manual")} className="text-[12px] text-[#64748B] hover:text-[#06B6D4] hover:underline" data-testid="company-skip-to-manual">
          Entreprise en cours de création / non référencée ? <span className="text-[#06B6D4] font-bold">Remplir manuellement</span>
        </button>
      </div>
    </div>
  );
}

/* (BriefSubmitModal removed — V1 focus on signed proposals only) */
